import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AuthShell, formatApiErrorDetail } from "../components/AuthShell";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [state, setState] = useState({ checking: true, valid: false, username: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState({ checking: false, valid: false, username: "" }); return; }
    axios.get(`${API}/api/auth/reset-password/validate`, { params: { token } })
      .then((r) => setState({ checking: false, valid: !!r.data?.valid, username: r.data?.username || "" }))
      .catch(() => setState({ checking: false, valid: false, username: "" }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return setError("Ο κωδικός πρέπει να έχει τουλάχιστον 8 χαρακτήρες.");
    if (pw !== pw2) return setError("Οι κωδικοί δεν ταιριάζουν.");
    setLoading(true); setError("");
    try {
      const r = await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pw });
      toast.success(r.data?.message || "Ο κωδικός άλλαξε.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell>
      <Link to="/login" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-login-link">
        <ArrowLeft className="w-3.5 h-3.5" /> Επιστροφή στη σύνδεση
      </Link>
      <h2 className="text-base md:text-lg font-semibold text-slate-900">Νέος κωδικός πρόσβασης</h2>
      {state.checking ? (
        <p className="text-sm text-slate-500 mt-6 flex items-center gap-2" data-testid="reset-checking"><Loader2 className="w-4 h-4 animate-spin" /> Έλεγχος συνδέσμου…</p>
      ) : !state.valid ? (
        <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-5 flex gap-3" data-testid="reset-invalid">
          <ShieldAlert className="w-6 h-6 text-red-600 shrink-0" />
          <div className="text-sm text-red-900">
            <p className="font-semibold">Ο σύνδεσμος δεν είναι έγκυρος ή έχει λήξει</p>
            <p className="mt-1">Ζητήστε <Link to="/forgot-password" className="underline" data-testid="reset-request-new-link">νέο σύνδεσμο επαναφοράς</Link>.</p>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5 mt-6" data-testid="reset-form">
          {state.username && <p className="text-sm text-slate-500">Λογαριασμός: <span className="font-medium text-slate-800" data-testid="reset-username">{state.username}</span></p>}
          <div className="space-y-2">
            <Label htmlFor="pw" className="text-slate-700">Νέος κωδικός (τουλάχιστον 8 χαρακτήρες)</Label>
            <div className="relative">
              <Input id="pw" type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required minLength={8} className="h-11 pr-10"
                autoComplete="new-password" data-testid="reset-password-input" />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="reset-toggle-password-btn">
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw2" className="text-slate-700">Επιβεβαίωση κωδικού</Label>
            <Input id="pw2" type={show ? "text" : "password"} value={pw2} onChange={(e) => setPw2(e.target.value)} required minLength={8} className="h-11"
              autoComplete="new-password" data-testid="reset-password-confirm-input" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full h-11 bg-slate-900 hover:bg-slate-800" data-testid="reset-submit-btn">
            {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <KeyRound className="w-5 h-5 mr-2" />} Αποθήκευση νέου κωδικού
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
